import { useState, useEffect, useCallback } from 'react';
import apiClient from '../../config/axiosConfig';

// Types
interface TopUser {
  user_id: number;
  user_name: string;
  user_surname: string;
  management: string;
  office: string;
  order_count: number;
  total_spent: number;
  average_order_amount: number;
}

interface UserStatsByDepartment {
  management: string;
  user_count: number;
  total_orders: number;
  total_spent: number;
  average_orders_per_user: number;
  average_spent_per_user: number;
}

interface UserStatsByOffice {
  office: string;
  management: string;
  user_count: number;
  total_orders: number;
  total_spent: number;
  average_orders_per_user: number;
  average_spent_per_user: number;
}

interface MostActiveUser {
  user_id: number;
  user_name: string;
  user_surname: string;
  management: string;
  office: string;
  recent_orders: number;
  recent_spent: number;
  last_order_date: string;
}

interface TopProduct {
  product_id: number;
  product_name: string;
  total_quantity: number;
  total_revenue: number;
  order_count: number;
  average_quantity_per_order: number;
}

interface ProductSalesByCategory {
  category_name: string;
  total_quantity: number;
  total_revenue: number;
  product_count: number;
  average_quantity_per_product: number;
}

interface ProductSalesTrend {
  date: string;
  total_quantity: number;
  total_revenue: number;
  order_count: number;
  unique_products: number;
}

interface MostPopularProduct {
  product_id: number;
  product_name: string;
  unique_users: number;
  total_quantity: number;
  total_revenue: number;
  average_quantity_per_user: number;
}

interface ProductSalesByDepartment {
  management: string;
  total_quantity: number;
  total_revenue: number;
  unique_users: number;
  average_quantity_per_user: number;
}

// Yeni tip
export interface UserProductConsumptionStat {
  user_id: number;
  user_name: string;
  user_surname: string;
  product_id: number;
  product_name: string;
  total_quantity: number;
  total_spent: number;
}

export const useStatistics = () => {
  // User Statistics
  const [topUsersByOrders, setTopUsersByOrders] = useState<TopUser[]>([]);
  const [topUsersBySpent, setTopUsersBySpent] = useState<TopUser[]>([]);
  const [userStatsByDepartment, setUserStatsByDepartment] = useState<UserStatsByDepartment[]>([]);
  const [userStatsByOffice, setUserStatsByOffice] = useState<UserStatsByOffice[]>([]);
  const [mostActiveUsers, setMostActiveUsers] = useState<MostActiveUser[]>([]);

  // Product Sales Statistics
  const [topProductsByQuantity, setTopProductsByQuantity] = useState<TopProduct[]>([]);
  const [topProductsByRevenue, setTopProductsByRevenue] = useState<TopProduct[]>([]);
  const [productSalesByCategory, setProductSalesByCategory] = useState<ProductSalesByCategory[]>([]);
  const [productSalesTrends, setProductSalesTrends] = useState<ProductSalesTrend[]>([]);
  const [mostPopularProducts, setMostPopularProducts] = useState<MostPopularProduct[]>([]);
  const [productSalesByDepartment, setProductSalesByDepartment] = useState<ProductSalesByDepartment[]>([]);

  // User Product Consumption Statistics
  const [userProductConsumptionStats, setUserProductConsumptionStats] = useState<UserProductConsumptionStat[]>([]);

  // Loading States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all statistics
  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all statistics in parallel
      const [
        topUsersByOrdersRes,
        topUsersBySpentRes,
        userStatsByDepartmentRes,
        userStatsByOfficeRes,
        mostActiveUsersRes,
        topProductsByQuantityRes,
        topProductsByRevenueRes,
        productSalesByCategoryRes,
        productSalesTrendsRes,
        mostPopularProductsRes,
        productSalesByDepartmentRes,
        userProductConsumptionStatsRes
      ] = await Promise.all([
        apiClient.get('/api/user-stats/top-by-orders?limit=10'),
        apiClient.get('/api/user-stats/top-by-spent?limit=10'),
        apiClient.get('/api/user-stats/by-department'),
        apiClient.get('/api/user-stats/by-office'),
        apiClient.get('/api/user-stats/most-active?days=30&limit=10'),
        apiClient.get('/api/product-sales-stats/top-by-quantity?limit=10'),
        apiClient.get('/api/product-sales-stats/top-by-revenue?limit=10'),
        apiClient.get('/api/product-sales-stats/by-category'),
        apiClient.get('/api/product-sales-stats/trends?days=7'),
        apiClient.get('/api/product-sales-stats/most-popular?limit=10'),
        apiClient.get('/api/product-sales-stats/by-department'),
        apiClient.get('/api/user-stats/product-consumption'),
      ]);

      // Set user statistics
      setTopUsersByOrders(topUsersByOrdersRes.data);
      setTopUsersBySpent(topUsersBySpentRes.data);
      setUserStatsByDepartment(userStatsByDepartmentRes.data);
      setUserStatsByOffice(userStatsByOfficeRes.data);
      setMostActiveUsers(mostActiveUsersRes.data);

      // Set product sales statistics
      setTopProductsByQuantity(topProductsByQuantityRes.data);
      setTopProductsByRevenue(topProductsByRevenueRes.data);
      setProductSalesByCategory(productSalesByCategoryRes.data);
      setProductSalesTrends(productSalesTrendsRes.data);
      setMostPopularProducts(mostPopularProductsRes.data);
      setProductSalesByDepartment(productSalesByDepartmentRes.data);
      setUserProductConsumptionStats(userProductConsumptionStatsRes.data);
    } catch (err) {
      console.error('Statistics fetch error:', err);
      const errorMessage = err instanceof Error ? err.message : 'İstatistikler yüklenirken hata oluştu';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return {
    // User Statistics
    topUsersByOrders,
    topUsersBySpent,
    userStatsByDepartment,
    userStatsByOffice,
    mostActiveUsers,
    
    // Product Sales Statistics
    topProductsByQuantity,
    topProductsByRevenue,
    productSalesByCategory,
    productSalesTrends,
    mostPopularProducts,
    productSalesByDepartment,
    userProductConsumptionStats,
    
    // Loading States
    loading,
    error,
    
    // Actions
    refreshData: fetchStatistics,
  };
}; 